import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { API_URL } from '../config';
import {
  Droplet,
  Sparkles,
  Calculator,
  AlertTriangle,
  Info,
  TrendingUp,
  TrendingDown,
  Minus,
  Brain,
  Activity,
  Wind,
  TestTube
} from 'lucide-react';
import { toast } from 'sonner';

export default function GazDuSangPage() {
  const [values, setValues] = useState({
    pH: '',
    PaCO2: '',
    HCO3: '',
    PaO2: '',
    Na: '',
    Cl: '',
    albumine: '4.0'
  });

  const [result, setResult] = useState(null);
  const [trouAnionique, setTrouAnionique] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [loading, setLoading] = useState(false);

  // Valeurs normales de référence
  const normalValues = {
    pH: { min: 7.35, max: 7.45 },
    PaCO2: { min: 35, max: 45 },
    HCO3: { min: 22, max: 26 },
    PaO2: { min: 70, max: 100 },
    trouAnionique: { min: 8, max: 12 }
  };

  const handleInputChange = (field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
  };

  const interpretGazDuSang = () => {
    const pH = parseFloat(values.pH);
    const PaCO2 = parseFloat(values.PaCO2);
    const HCO3 = parseFloat(values.HCO3);

    if (!pH || !PaCO2 || !HCO3) {
      toast.error('Veuillez remplir pH, PaCO2 et HCO3');
      return;
    }

    let interpretation = {
      type: '',
      severity: '',
      compensation: '',
      description: '',
      etiologies: [],
      color: ''
    };

    // Déterminer le trouble primaire
    if (pH < normalValues.pH.min) {
      // Acidose
      if (PaCO2 > normalValues.PaCO2.max) {
        interpretation.type = 'Acidose Respiratoire';
        interpretation.color = 'text-red-400';
        
        if (HCO3 > normalValues.HCO3.max) {
          interpretation.compensation = 'Compensée (chronique)';
          interpretation.severity = 'modérée';
          interpretation.etiologies = [
            'Insuffisance respiratoire chronique',
            'BPCO avancée',
            'Syndrome obésité-hypoventilation'
          ];
        } else {
          interpretation.compensation = 'Non compensée (aiguë)';
          interpretation.severity = 'sévère';
          interpretation.etiologies = [
            'Hypoventilation alvéolaire',
            'Insuffisance respiratoire aiguë',
            'Décompensation de BPCO',
            'Dépression respiratoire (médicamenteuse)',
            'Obstruction des voies aériennes'
          ];
        }
        interpretation.description = 'Accumulation de CO2 par hypoventilation alvéolaire';
      } else if (HCO3 < normalValues.HCO3.min) {
        interpretation.type = 'Acidose Métabolique';
        interpretation.color = 'text-orange-400';
        
        if (PaCO2 < normalValues.PaCO2.min) {
          interpretation.compensation = 'Compensée partiellement';
          interpretation.severity = 'modérée';
        } else {
          interpretation.compensation = 'Non compensée';
          interpretation.severity = 'sévère';
        }
        
        interpretation.description = 'Diminution des bicarbonates';
        interpretation.etiologies = [
          'Acidocétose (diabète, jeûne, alcoolisme)',
          'Acidose lactique (hypoxie, choc)',
          'Insuffisance rénale',
          'Intoxication (méthanol, éthylène glycol, salicylés)',
          'Diarrhées profuses',
          'Acidose tubulaire rénale'
        ];
      }
    } else if (pH > normalValues.pH.max) {
      // Alcalose
      if (PaCO2 < normalValues.PaCO2.min) {
        interpretation.type = 'Alcalose Respiratoire';
        interpretation.color = 'text-blue-400';
        
        if (HCO3 < normalValues.HCO3.min) {
          interpretation.compensation = 'Compensée (chronique)';
          interpretation.severity = 'modérée';
        } else {
          interpretation.compensation = 'Non compensée (aiguë)';
          interpretation.severity = 'légère';
        }
        
        interpretation.description = 'Hyperventilation alvéolaire avec élimination excessive de CO2';
        interpretation.etiologies = [
          'Hyperventilation (anxiété, douleur)',
          'Hypoxémie (altitude, pneumonie)',
          'Fièvre',
          'Insuffisance hépatique',
          'Ventilation mécanique excessive',
          'Embolie pulmonaire',
          'Crise d\'asthme (stade précoce)'
        ];
      } else if (HCO3 > normalValues.HCO3.max) {
        interpretation.type = 'Alcalose Métabolique';
        interpretation.color = 'text-cyan-400';
        
        if (PaCO2 > normalValues.PaCO2.max) {
          interpretation.compensation = 'Compensée partiellement';
          interpretation.severity = 'modérée';
        } else {
          interpretation.compensation = 'Non compensée';
          interpretation.severity = 'légère';
        }
        
        interpretation.description = 'Augmentation des bicarbonates ou perte d\'acides';
        interpretation.etiologies = [
          'Vomissements prolongés',
          'Aspiration nasogastrique',
          'Traitement diurétique',
          'Hypokaliémie',
          'Hyperaldostéronisme',
          'Syndrome de Cushing'
        ];
      }
    } else {
      // pH normal
      if (PaCO2 > normalValues.PaCO2.max && HCO3 > normalValues.HCO3.max) {
        interpretation.type = 'Acidose Respiratoire Compensée';
        interpretation.compensation = 'Totalement compensée';
        interpretation.severity = 'compensée';
        interpretation.color = 'text-yellow-400';
        interpretation.description = 'État chronique stabilisé';
        interpretation.etiologies = [
          'Insuffisance respiratoire chronique stable',
          'BPCO stable'
        ];
      } else if (PaCO2 < normalValues.PaCO2.min && HCO3 < normalValues.HCO3.min) {
        interpretation.type = 'Alcalose Respiratoire Compensée';
        interpretation.compensation = 'Totalement compensée';
        interpretation.severity = 'compensée';
        interpretation.color = 'text-green-400';
        interpretation.description = 'État chronique stabilisé';
        interpretation.etiologies = [
          'Vie en altitude',
          'Hyperventilation chronique'
        ];
      } else {
        interpretation.type = 'Équilibre Acido-Basique Normal';
        interpretation.compensation = 'Aucune';
        interpretation.severity = 'normal';
        interpretation.color = 'text-green-400';
        interpretation.description = 'Tous les paramètres dans les normes';
        interpretation.etiologies = [];
      }
    }

    setResult(interpretation);
    toast.success('Interprétation effectuée');
  };

  const calculateTrouAnionique = () => {
    const Na = parseFloat(values.Na);
    const Cl = parseFloat(values.Cl);
    const HCO3 = parseFloat(values.HCO3);
    const albumine = parseFloat(values.albumine);

    if (!Na || !Cl || !HCO3) {
      toast.error('Na, Cl et HCO3 requis pour le trou anionique');
      return;
    }

    let trou = Na - (Cl + HCO3);
    
    // Correction pour hypoalbuminémie
    if (albumine && albumine < 4.0) {
      const correction = (4.0 - albumine) * 2.5;
      trou += correction;
    }

    let interpretation = '';
    let causes = [];

    if (trou > normalValues.trouAnionique.max) {
      interpretation = 'Trou anionique élevé';
      causes = [
        'Acidose lactique (hypoxie, choc, sepsis)',
        'Acidocétose diabétique',
        'Insuffisance rénale (urémie)',
        'Intoxication : méthanol, éthylène glycol, salicylés',
        'Acidocétose alcoolique'
      ];
    } else if (trou < normalValues.trouAnionique.min) {
      interpretation = 'Trou anionique bas';
      causes = [
        'Hypoalbuminémie',
        'Hypercalcémie',
        'Hypermagnésémie',
        'Myélome multiple (protéines cationiques)'
      ];
    } else {
      interpretation = 'Trou anionique normal';
      causes = [
        'Perte digestive de bicarbonates (diarrhée)',
        'Acidose tubulaire rénale',
        'Acidose par surcharge chlorée (NaCl IV)'
      ];
    }

    setTrouAnionique({
      value: trou.toFixed(1),
      interpretation,
      causes
    });

    toast.success('Trou anionique calculé');
  };

  const handleAiAnalysis = async () => {
    if (!aiPrompt.trim() && !result) {
      toast.error('Veuillez décrire le cas clinique ou effectuer une interprétation');
      return;
    }

    setLoading(true);
    try {
      let context = `Analyse de gaz du sang artériel:\n\n`;
      
      if (values.pH) context += `pH: ${values.pH}\n`;
      if (values.PaCO2) context += `PaCO2: ${values.PaCO2} mmHg\n`;
      if (values.HCO3) context += `HCO3: ${values.HCO3} mmol/L\n`;
      if (values.PaO2) context += `PaO2: ${values.PaO2} mmHg\n`;
      if (trouAnionique) context += `Trou anionique: ${trouAnionique.value} mEq/L\n\n`;
      
      if (result) {
        context += `Interprétation automatique: ${result.type}\n`;
        context += `Compensation: ${result.compensation}\n\n`;
      }
      
      context += `Contexte clinique:\n${aiPrompt}`;

      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: context,
          context: 'gaz_du_sang_interpretation'
        })
      });

      if (!response.ok) throw new Error('Erreur lors de l\'analyse IA');

      const data = await response.json();
      setAiResponse(data.response);
      toast.success('Analyse IA terminée');
    } catch (error) {
      console.error('AI Analysis Error:', error);
      toast.error('Erreur lors de l\'analyse IA');
      setAiResponse('Erreur lors de l\'analyse. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (value, min, max) => {
    const val = parseFloat(value);
    if (!val) return <Minus className="w-4 h-4 text-gray-400" />;
    if (val < min) return <TrendingDown className="w-4 h-4 text-red-400" />;
    if (val > max) return <TrendingUp className="w-4 h-4 text-orange-400" />;
    return <Activity className="w-4 h-4 text-green-400" />;
  };

  // Référence théorique
  const referenceData = {
    interpretation: [
      {
        titre: 'Acidose Respiratoire',
        pH: '↓ < 7.35',
        PaCO2: '↑ > 45',
        HCO3: 'N ou ↑',
        description: 'Hypoventilation alvéolaire',
        causes: ['BPCO décompensée', 'Insuffisance respiratoire aiguë', 'Obstruction voies aériennes', 'Dépression respiratoire']
      },
      {
        titre: 'Acidose Métabolique',
        pH: '↓ < 7.35',
        PaCO2: 'N ou ↓',
        HCO3: '↓ < 22',
        description: 'Perte de bases ou gain d\'acides',
        causes: ['Acidocétose', 'Acidose lactique', 'Insuffisance rénale', 'Diarrhées']
      },
      {
        titre: 'Alcalose Respiratoire',
        pH: '↑ > 7.45',
        PaCO2: '↓ < 35',
        HCO3: 'N ou ↓',
        description: 'Hyperventilation alvéolaire',
        causes: ['Anxiété', 'Douleur', 'Hypoxémie', 'Ventilation mécanique excessive']
      },
      {
        titre: 'Alcalose Métabolique',
        pH: '↑ > 7.45',
        PaCO2: 'N ou ↑',
        HCO3: '↑ > 26',
        description: 'Perte d\'acides ou gain de bases',
        causes: ['Vomissements', 'Aspiration gastrique', 'Diurétiques', 'Hypokaliémie']
      }
    ]
  };

  return (
    <div className="space-y-6" data-testid="gaz-du-sang-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold glow-text flex items-center gap-3">
            <Droplet className="w-8 h-8 text-cyan-400" />
            Gaz du Sang
          </h1>
          <p className="text-[hsl(var(--text-dim))] mt-2">
            Interprétation interactive avec calculateur et analyse IA
          </p>
        </div>
      </div>

      {/* Alert Valeurs Normales */}
      <Alert className="bg-cyan-500/10 border-cyan-500/30">
        <Info className="w-5 h-5 text-cyan-400" />
        <AlertDescription className="text-cyan-300">
          <strong>Valeurs normales :</strong> pH 7.35-7.45 | PaCO₂ 35-45 mmHg | HCO₃⁻ 22-26 mmol/L | PaO₂ 70-100 mmHg
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calculateur */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-cyan-400" />
                Calculateur & Interprétation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Paramètres Principaux */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    {getStatusIcon(values.pH, normalValues.pH.min, normalValues.pH.max)}
                    pH
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="7.40"
                    value={values.pH}
                    onChange={(e) => handleInputChange('pH', e.target.value)}
                    className="text-lg font-semibold"
                  />
                  <p className="text-xs text-[hsl(var(--text-dim))]">Normal: 7.35-7.45</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    {getStatusIcon(values.PaCO2, normalValues.PaCO2.min, normalValues.PaCO2.max)}
                    PaCO₂
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="40"
                    value={values.PaCO2}
                    onChange={(e) => handleInputChange('PaCO2', e.target.value)}
                    className="text-lg font-semibold"
                  />
                  <p className="text-xs text-[hsl(var(--text-dim))]">Normal: 35-45 mmHg</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    {getStatusIcon(values.HCO3, normalValues.HCO3.min, normalValues.HCO3.max)}
                    HCO₃⁻
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="24"
                    value={values.HCO3}
                    onChange={(e) => handleInputChange('HCO3', e.target.value)}
                    className="text-lg font-semibold"
                  />
                  <p className="text-xs text-[hsl(var(--text-dim))]">Normal: 22-26 mmol/L</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    {getStatusIcon(values.PaO2, normalValues.PaO2.min, normalValues.PaO2.max)}
                    PaO₂
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="90"
                    value={values.PaO2}
                    onChange={(e) => handleInputChange('PaO2', e.target.value)}
                    className="text-lg font-semibold"
                  />
                  <p className="text-xs text-[hsl(var(--text-dim))]">Normal: 70-100 mmHg</p>
                </div>
              </div>

              <Button
                onClick={interpretGazDuSang}
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500"
                data-testid="interpret-button"
              >
                <TestTube className="w-4 h-4 mr-2" />
                Interpréter
              </Button>

              {/* Résultat Interprétation */}
              {result && (
                <div className="p-6 bg-[hsl(var(--card-hover))] rounded-lg border border-cyan-500/30 space-y-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className={`text-2xl font-bold ${result.color}`}>
                        {result.type}
                      </h3>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          {result.compensation}
                        </Badge>
                        <Badge 
                          variant={result.severity === 'sévère' ? 'destructive' : 'secondary'}
                          className="text-xs"
                        >
                          {result.severity}
                        </Badge>
                      </div>
                    </div>
                    <Wind className={`w-8 h-8 ${result.color}`} />
                  </div>

                  <p className="text-white">{result.description}</p>

                  {result.etiologies.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-cyan-300 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Étiologies possibles :
                      </h4>
                      <ul className="space-y-1">
                        {result.etiologies.map((etio, idx) => (
                          <li key={idx} className="text-sm text-[hsl(var(--text-dim))] flex items-start gap-2">
                            <span className="text-cyan-400 mt-1">•</span>
                            <span>{etio}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Trou Anionique */}
              <div className="border-t border-[hsl(var(--border))] pt-6 space-y-4">
                <h4 className="font-semibold text-white flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-purple-400" />
                  Trou Anionique (optionnel)
                </h4>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Na⁺</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="140"
                      value={values.Na}
                      onChange={(e) => handleInputChange('Na', e.target.value)}
                    />
                    <p className="text-xs text-[hsl(var(--text-dim))]">mmol/L</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Cl⁻</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="100"
                      value={values.Cl}
                      onChange={(e) => handleInputChange('Cl', e.target.value)}
                    />
                    <p className="text-xs text-[hsl(var(--text-dim))]">mmol/L</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Albumine</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="4.0"
                      value={values.albumine}
                      onChange={(e) => handleInputChange('albumine', e.target.value)}
                    />
                    <p className="text-xs text-[hsl(var(--text-dim))]">g/dL</p>
                  </div>
                </div>

                <Button
                  onClick={calculateTrouAnionique}
                  variant="outline"
                  className="w-full"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculer le Trou Anionique
                </Button>

                {trouAnionique && (
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[hsl(var(--text-dim))]">Trou anionique :</span>
                      <span className="text-2xl font-bold text-purple-400">
                        {trouAnionique.value} mEq/L
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-white">{trouAnionique.interpretation}</p>
                    <p className="text-xs text-[hsl(var(--text-dim))]">Normal: 8-12 mEq/L</p>
                    
                    {trouAnionique.causes.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-purple-300 mb-1">Causes possibles :</p>
                        <ul className="space-y-1">
                          {trouAnionique.causes.map((cause, idx) => (
                            <li key={idx} className="text-xs text-[hsl(var(--text-dim))] flex items-start gap-2">
                              <span className="text-purple-400">•</span>
                              <span>{cause}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Référence Théorique */}
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-cyan-400" />
                Tableau de Référence
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {referenceData.interpretation.map((ref, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-[hsl(var(--card-hover))] rounded-lg border border-[hsl(var(--border))]"
                  >
                    <h4 className="font-semibold text-white mb-2">{ref.titre}</h4>
                    <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                      <div>
                        <span className="text-[hsl(var(--text-dim))]">pH:</span>{' '}
                        <span className="font-mono text-cyan-300">{ref.pH}</span>
                      </div>
                      <div>
                        <span className="text-[hsl(var(--text-dim))]">PaCO₂:</span>{' '}
                        <span className="font-mono text-cyan-300">{ref.PaCO2}</span>
                      </div>
                      <div>
                        <span className="text-[hsl(var(--text-dim))]">HCO₃⁻:</span>{' '}
                        <span className="font-mono text-cyan-300">{ref.HCO3}</span>
                      </div>
                    </div>
                    <p className="text-xs text-[hsl(var(--text-dim))] mb-2">{ref.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {ref.causes.map((cause, cidx) => (
                        <Badge key={cidx} variant="secondary" className="text-[10px]">
                          {cause}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Panel IA */}
        <div className="space-y-4">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                Analyse IA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Décrivez le contexte clinique du patient...&#10;&#10;Exemple: Patient de 65 ans, dyspnée, antécédents de BPCO. SpO2 88% sous O2 2L/min. Tachypnée 28/min."
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={8}
                className="resize-none"
              />

              <Button
                onClick={handleAiAnalysis}
                disabled={loading || (!aiPrompt.trim() && !result)}
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyser avec IA
                  </>
                )}
              </Button>

              {aiResponse && (
                <div className="p-4 bg-[hsl(var(--card-hover))] rounded-lg border border-cyan-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-5 h-5 text-cyan-400" />
                    <span className="font-semibold text-white">Recommandation IA</span>
                  </div>
                  <div className="text-sm text-white whitespace-pre-wrap leading-relaxed">
                    {aiResponse}
                  </div>
                  <Alert className="mt-3 bg-yellow-500/10 border-yellow-500/30">
                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    <AlertDescription className="text-yellow-300 text-xs">
                      Cette analyse IA est une aide à la décision. Elle ne remplace pas le jugement clinique.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
